import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GitBranch,
  ChevronDown,
  Check,
  Loader2,
  Plus,
  Search,
} from "lucide-react";
import { api } from "@/lib/api";

interface BranchItem {
  name: string;
  is_current: boolean;
}

interface BranchSwitcherProps {
  projectId: string;
  projectName: string;
  repoFullName: string;
  currentBranch: string | null;
  onBranchSwitch: (branch: string, created: boolean) => void;
}

export function BranchSwitcher({
  projectId,
  projectName,
  repoFullName,
  currentBranch,
  onBranchSwitch,
}: BranchSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [switching, setSwitching] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newBranch, setNewBranch] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState<number>(0);

  // Calculate dropdown position when opening or on window resize
  useEffect(() => {
    if (!open || !btnRef.current) return;

    function updatePosition() {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  // Load branches when dropdown opens
  useEffect(() => {
    if (!open || !repoFullName) return;
    setLoading(true);
    api.projects
      .listBranches(projectId)
      .then(setBranches)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, projectId, repoFullName]);

  // Measure list content height whenever content changes
  useLayoutEffect(() => {
    if (!listRef.current) return;
    setListHeight(listRef.current.scrollHeight);
  }, [loading, branches, filter]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setCreating(false);
        setFilter("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        setCreating(false);
        setFilter("");
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  async function handleSwitch(branch: string) {
    if (branch === currentBranch) {
      setOpen(false);
      return;
    }
    setSwitching(branch);
    try {
      const result = await api.projects.switchBranch(projectId, branch);
      if (result.success) {
        onBranchSwitch(result.branch, false);
        setOpen(false);
        setFilter("");
      }
    } catch {
      // Silent fail
    } finally {
      setSwitching(null);
    }
  }

  async function handleCreate() {
    if (!newBranch.trim()) return;
    setSwitching(newBranch);
    try {
      const result = await api.projects.switchBranch(
        projectId,
        newBranch.trim(),
        true,
      );
      if (result.success) {
        onBranchSwitch(result.branch, true);
        setOpen(false);
        setCreating(false);
        setNewBranch("");
        setFilter("");
      }
    } catch {
      // Silent fail
    } finally {
      setSwitching(null);
    }
  }

  const filtered = branches.filter((b) =>
    b.name.toLowerCase().includes(filter.toLowerCase()),
  );

  const canSwitch = !!repoFullName;

  // Clamp measured height to max-h-64 (256px)
  const clampedHeight = Math.min(listHeight, 256);

  return (
    <div ref={ref} className="flex-1 min-w-0 flex justify-center">
      {/* Trigger */}
      <button
        ref={btnRef}
        onClick={() => canSwitch && setOpen(!open)}
        className={`flex items-center gap-1.5 min-w-0 px-2.5 py-1 rounded-lg transition-colors ${
          canSwitch
            ? "hover:bg-white/[0.06] cursor-pointer"
            : "cursor-default"
        }`}
      >
        <span className="text-sm font-medium text-daydra-text truncate">
          {projectName}
        </span>
        {currentBranch && (
          <>
            <span className="text-daydra-muted text-sm">·</span>
            <GitBranch size={12} className="text-daydra-muted shrink-0" />
            <span className="text-sm text-daydra-dim truncate max-w-[120px]">
              {currentBranch}
            </span>
          </>
        )}
        {canSwitch && (
          <ChevronDown
            size={12}
            className={`text-daydra-muted shrink-0 transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.95, y: -4 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.95, y: -4 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed w-64 z-50 bg-daydra-surface backdrop-blur-md rounded-2xl shadow-daydra-glow ring-1 ring-white/[0.06] overflow-hidden"
            style={{
              transformOrigin: "top center",
              top: `${dropdownPos.top}px`,
              left: `${dropdownPos.left - 128}px`,
            }}
          >
            {/* Filter */}
            <div className="flex items-center gap-2.5 px-3.5 py-3 border-b border-white/[0.06]">
              <Search size={16} className="text-daydra-muted shrink-0" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter branches..."
                className="w-full text-sm bg-transparent text-daydra-text placeholder:text-daydra-muted outline-none"
                autoFocus
              />
            </div>

            {/* Branch list — smooth height transition */}
            <div
              className="overflow-hidden transition-[height] duration-300 ease-in-out"
              style={{ height: `${clampedHeight}px` }}
            >
              <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: "256px" }}>
                {loading ? (
                  <div className="flex items-center justify-center py-5">
                    <Loader2 size={16} className="animate-spin text-daydra-muted" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="px-3.5 py-3 text-sm text-daydra-muted">
                    No branches found
                  </div>
                ) : (
                  filtered.map((b) => (
                    <button
                      key={b.name}
                      onClick={() => handleSwitch(b.name)}
                      disabled={switching !== null}
                      className="flex items-center gap-2.5 w-full px-3.5 py-3 text-sm hover:bg-white/[0.04] transition-colors disabled:opacity-50"
                    >
                      {b.name === currentBranch ? (
                        <Check size={16} className="text-daydra-accent shrink-0" />
                      ) : (
                        <span className="w-4 shrink-0" />
                      )}
                      <span className="text-daydra-dim truncate">{b.name}</span>
                      {switching === b.name && (
                        <Loader2
                          size={14}
                          className="animate-spin text-daydra-muted ml-auto shrink-0"
                        />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Create new branch */}
            <div className="border-t border-white/[0.06] px-3.5 py-3">
              {creating ? (
                <div className="flex items-center gap-2.5">
                  <input
                    type="text"
                    value={newBranch}
                    onChange={(e) => setNewBranch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") {
                        setCreating(false);
                        setNewBranch("");
                      }
                    }}
                    placeholder="new-branch-name"
                    className="flex-1 text-sm bg-transparent text-daydra-text placeholder:text-daydra-muted outline-none min-w-0"
                    autoFocus
                  />
                  <button
                    onClick={handleCreate}
                    disabled={!newBranch.trim() || switching !== null}
                    className="text-sm font-medium text-daydra-accent hover:text-daydra-accent/80 disabled:opacity-50 shrink-0"
                  >
                    {switching ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setCreating(true)}
                  className="flex items-center gap-2 text-sm text-daydra-muted hover:text-daydra-accent transition-colors"
                >
                  <Plus size={16} />
                  <span>New branch</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
